/**
 * Lightweight client-side error monitor.
 * Captures uncaught errors and unhandled promise rejections.
 * Logs to console and (optionally) forwards to an endpoint via VITE_ERROR_REPORT_URL.
 */

type ReportPayload = {
  type: "error" | "unhandledrejection";
  message: string;
  stack?: string;
  source?: string;
  lineno?: number;
  colno?: number;
  url: string;
  userAgent: string;
  ts: string;
};

const seen = new Set<string>();

function send(payload: ReportPayload) {
  // Always log so it's visible in browser devtools
  // eslint-disable-next-line no-console
  console.error("[monitor]", payload);

  const endpoint = import.meta.env.VITE_ERROR_REPORT_URL as string | undefined;
  if (!endpoint) return;
  try {
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon) {
      navigator.sendBeacon(endpoint, body);
    } else {
      fetch(endpoint, { method: "POST", body, keepalive: true, headers: { "content-type": "application/json" } }).catch(() => {});
    }
  } catch {
    /* swallow */
  }
}

function dedupeKey(p: ReportPayload) {
  return `${p.type}|${p.message}|${p.source ?? ""}|${p.lineno ?? ""}`;
}

let installed = false;
export function installErrorMonitor() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event) => {
    const payload: ReportPayload = {
      type: "error",
      message: event.message || String(event.error),
      stack: event.error?.stack,
      source: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      url: window.location.href,
      userAgent: navigator.userAgent,
      ts: new Date().toISOString(),
    };
    const key = dedupeKey(payload);
    if (seen.has(key)) return;
    seen.add(key);
    send(payload);
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason as { message?: string; stack?: string } | string | undefined;
    const message = typeof reason === "string" ? reason : reason?.message ?? "Unhandled promise rejection";
    const payload: ReportPayload = {
      type: "unhandledrejection",
      message,
      stack: typeof reason === "object" ? reason?.stack : undefined,
      url: window.location.href,
      userAgent: navigator.userAgent,
      ts: new Date().toISOString(),
    };
    const key = dedupeKey(payload);
    if (seen.has(key)) return;
    seen.add(key);
    send(payload);
  });
}
